import useNotificationsStore from '@/store/notifications';
import { NotificationsTypes } from '@/types';

class BaseAPIHelper {
  requestOptions: any = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRFToken': CSRF_TOKEN
    },
    mode: 'same-origin'
  };
  apiBaseUrl: string = '';

  constructor(apiBaseUrl: string) {
    this.apiBaseUrl = apiBaseUrl;
  }

  errorsHandler(errorData: any) {
    if (errorData && Object.keys(errorData).length > 0) {
      const notificationsStore = useNotificationsStore();

      if (errorData.error_message) {
        notificationsStore.add({
          message: errorData.error_message,
          type: NotificationsTypes.error,
        });
      } else if (errorData.detail) {
        notificationsStore.add({
          message: errorData.detail,
          type: NotificationsTypes.error,
        });
      }
    }
  }

  async get(url: string): Promise<any> {
    let response = {};

    const options = {
      ...this.requestOptions,
     headers: new Headers({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRFToken': CSRF_TOKEN
      }),
      method: 'GET',
    };

    await fetch(`${this.apiBaseUrl}${url}`, options)
      .then((res) => res.json())
      .then((obj) => {
        response = obj;
      })
      .catch((error) => {
        response = error.data;
      });

    if (
      !(
        (url === '/user-profile/me/' && response?.detail === 'Authentication credentials were not provided.') ||
        (url === '/customer-company/me/' && response?.detail === 'Company not found for this user.')
      )
    ) {
      this.errorsHandler(response);
    }

    return Promise.resolve(response);
  }

  async post(url: string, params: any): Promise<any> {
    let response: any = {};

    // Create a completely new options object instead of spreading requestOptions
    const options = {
      method: 'POST',
      ...this.requestOptions,
      headers: new Headers({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRFToken': CSRF_TOKEN
      }),
      body: params ? JSON.stringify(params) : null
    };

    try {
      const res = await fetch(`${this.apiBaseUrl}${url}`, options);
      const data = await res.json();

      response = {
        status: res.status,
        data: data, 
      };
    } catch (error) {
      response = {
        status: error.status || 500,
        data: error.data || error.message, 
      };
    }

    this.errorsHandler(response.data);
    return response;
  }

  async postFormData(url: string, params: any): Promise<any> {
    let response = {};

    const data = new FormData();

    Object.keys(params).forEach((key) => {
      data.append(key, params[key]);
    });

    const headersObg = { ...this.requestOptions };
    delete headersObg.headers['Content-Type'];

    const options = {
      ...headersObg,
      method: 'POST',
      body: data,
    };

    await fetch(`${this.apiBaseUrl}${url}`, options)
      .then((res) => res.json())
      .then((obj) => {
        response = obj;
      })
      .catch((error) => {
        response = error.data;
      });

    this.errorsHandler(response);
    return Promise.resolve(response);
  }

  async postFormDataXMLHttpRequest(url: string, params: any, onUploadProgress?: (event: ProgressEvent) => void): Promise<any> {
    return new Promise((resolve, reject) => {
      const data = new FormData();
      Object.keys(params).forEach((key) => {
        data.append(key, params[key]);
      });

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.apiBaseUrl}${url}`, true);

      // Copy headers except for Content-Type
      if (this.requestOptions?.headers) {
        Object.entries(this.requestOptions.headers).forEach(([key, value]) => {
          if (key.toLowerCase() !== 'content-type') {
            xhr.setRequestHeader(key, String(value));
          }
        });
      }

      if (onUploadProgress) {
        xhr.upload.onprogress = onUploadProgress;
      }

      xhr.onload = () => {
        let response;
        try {
          response = JSON.parse(xhr.responseText);
        } catch (e) {
          response = { error: 'Invalid JSON response' };
        }
        this.errorsHandler(response);
        resolve(response);
      };

      xhr.onerror = () => {
        reject({ error: 'Upload failed' });
      };

      xhr.send(data);
    });
  }

  async postUrlEncoded(url: string, params: any): Promise<any> {
    let response = {};

    const data = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      data.append(key, params[key]);
    });

    const options = {
      ...this.requestOptions,
      method: 'POST',
      headers: {
        ...this.requestOptions.headers,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: data.toString(),
    };

    await fetch(`${this.apiBaseUrl}${url}`, options)
      .then((res) => res.json())
      .then((obj) => {
        response = obj;
      })
      .catch((error) => {
        response = error.data;
      });

    this.errorsHandler(response);
    return Promise.resolve(response);
  }

  async put(url: string, params: any): Promise<any> {
    let response = {};

    const options = {
      ...this.requestOptions,
      method: 'PUT',
      body: JSON.stringify(params),
    };

    await fetch(`${this.apiBaseUrl}${url}`, options)
      .then((res) => res.json())
      .then((obj) => {
        response = obj;
      })
      .catch((error) => {
        response = error.data;
      });

    this.errorsHandler(response);
    return Promise.resolve(response);
  }

  async patch(url: string, params: any): Promise<any> {
    let response = {};

    const options = {
      ...this.requestOptions,
      method: 'PATCH',
      body: JSON.stringify(params),
    };

    await fetch(`${this.apiBaseUrl}${url}`, options)
      .then((res) => res.json())
      .then((obj) => {
        response = obj;
      })
      .catch((error) => {
        response = error.data;
      });

    this.errorsHandler(response);
    return Promise.resolve(response);
  }

  async delete(url: string): Promise<any> {
    let response = {};

    const options = {
      ...this.requestOptions,
      method: 'DELETE',
    };

    await fetch(`${this.apiBaseUrl}${url}`, options)
      .then((res) => res.json())
      .then((obj) => {
        response = obj;
      })
      .catch((error) => {
        response = error.data;
      });

    this.errorsHandler(response);
    return Promise.resolve(response);
  }
}

export default new BaseAPIHelper('');

let admin_api_url = '';
let website_api_url = '';

if (typeof ADMIN_API_URL !== 'undefined') {
  admin_api_url = ADMIN_API_URL;
}

if (typeof WEBSITE_API_URL !== 'undefined') {
  website_api_url = WEBSITE_API_URL;
}

export const AdminApiHelper = new BaseAPIHelper(admin_api_url);
export const WebsiteApiHelper = new BaseAPIHelper(website_api_url);
